import React, {useEffect, useMemo, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import DayPicker from "react-day-picker"
import cx from "classnames"
import {SecondaryButton} from "components/buttons"
import {Icon} from "components/Icon"
import moment from "moment"
import styles from "./CalendarPicker.module.css"
import "./CustomPanelDatePicker.css"
import {DisplayMode} from "types/common"

type CalendarPickerProps = {
    selectedDay?: Date
    onDayClick?: Function
    displayMode?: DisplayMode
    highlightedRange?: {
        startDate: Date
        endDate: Date
    }
    meetingDateRange?: {
        startDate: Date
        endDate: Date
    }
}

export const CalendarPicker = (props: CalendarPickerProps) => {
    const {selectedDay, onDayClick, displayMode = "normal", highlightedRange, meetingDateRange} = props
    const {t} = useTranslation(["calendar"])
    const weekdaysShort = [
        t("calendar.weekdays.Sun"),
        t("calendar.weekdays.Mon"),
        t("calendar.weekdays.Tue"),
        t("calendar.weekdays.Wed"),
        t("calendar.weekdays.Thu"),
        t("calendar.weekdays.Fri"),
        t("calendar.weekdays.Sat")
    ]
    const pickerRef = useRef(null)
    const [today, setToday] = useState(selectedDay ? selectedDay : new Date())
    const [dateViewed, setDateViewed] = useState(selectedDay ? selectedDay : new Date())
    const isPanelDisplay = displayMode === "panel"

    useEffect(() => {
        setToday(selectedDay)
        setDateViewed(selectedDay)
    }, [selectedDay])

    const onClick = (date) => {
        if (disabledDays && disabledDays(date)) {
            return
        }
        if (onDayClick) {
            onDayClick(date)
        }
    }

    const onClickPrevMonth = () => {
        if (isPrevMonthDisabled) return
        pickerRef.current.showPreviousMonth()
        const prevMonth = moment(dateViewed).subtract(1, "month").toDate()
        setDateViewed(prevMonth)
    }

    const onClickNextMonth = () => {
        if (isNextMonthDisabled) return
        pickerRef.current.showNextMonth()
        const nextMonth = moment(dateViewed).add(1, "month").toDate()
        setDateViewed(nextMonth)
    }

    const isPrevMonthDisabled = useMemo(() => {
        if (!meetingDateRange?.startDate || !meetingDateRange?.endDate) {
            return false
        }
        const now = moment()
        const meetingStart = moment(meetingDateRange.startDate).startOf("day")
        const meetingEnd = moment(meetingDateRange.endDate).endOf("day")

        if (now.isBefore(meetingStart) || now.isAfter(meetingEnd)) {
            return true
        }

        const prevMonth = moment(dateViewed).subtract(1, "month")
        const meetingStartMonth = moment(meetingStart).startOf("month")
        return prevMonth.isBefore(meetingStartMonth)
    }, [meetingDateRange, dateViewed])

    const isNextMonthDisabled = useMemo(() => {
        if (!meetingDateRange?.startDate || !meetingDateRange?.endDate) {
            return false
        }
        const now = moment()
        const meetingStart = moment(meetingDateRange.startDate).startOf("day")
        const meetingEnd = moment(meetingDateRange.endDate).endOf("day")

        if (now.isBefore(meetingStart) || now.isAfter(meetingEnd)) {
            return true
        }

        const nextMonth = moment(dateViewed).add(1, "month")
        const meetingEndMonth = moment(meetingEnd).endOf("month")
        return nextMonth.isAfter(meetingEndMonth)
    }, [meetingDateRange, dateViewed])

    const disabledDays = useMemo(() => {
        if (!meetingDateRange?.startDate || !meetingDateRange?.endDate) {
            return undefined
        }
        return (date: Date) => {
            return (
                moment(date).isBefore(moment(meetingDateRange.startDate).startOf("day")) ||
                moment(date).isAfter(moment(meetingDateRange.endDate).endOf("day"))
            )
        }
    }, [meetingDateRange])

    const normalizedHighlightedRange = useMemo(() => {
        if (!highlightedRange?.startDate || !highlightedRange?.endDate) {
            return null
        }
        const start =
            highlightedRange.startDate <= highlightedRange.endDate
                ? highlightedRange.startDate
                : highlightedRange.endDate
        const end =
            highlightedRange.startDate <= highlightedRange.endDate
                ? highlightedRange.endDate
                : highlightedRange.startDate
        return {startDate: start, endDate: end}
    }, [highlightedRange])

    const modifiers = useMemo(() => {
        const base: Record<string, any> = {
            todaySelected: today
        }
        if (normalizedHighlightedRange && today) {
            base.meetingRange = (date: Date) => {
                const dateStr = moment(date).format("YYYY-MM-DD")
                const todayStr = moment(today).format("YYYY-MM-DD")

                if (dateStr === todayStr) {
                    return false
                }

                const dateStartOfDay = moment(date).startOf("day").toDate()
                const rangeStart = moment(normalizedHighlightedRange.startDate).startOf("day").toDate()
                const rangeEnd = moment(normalizedHighlightedRange.endDate).startOf("day").toDate()

                const isInRange = dateStartOfDay >= rangeStart && dateStartOfDay <= rangeEnd

                if (!isInRange) {
                    return false
                }

                const todayDate = moment().startOf("day").toDate()
                if (todayDate > rangeEnd) {
                    return false
                }

                return true
            }
        } else if (normalizedHighlightedRange) {
            const todayDate = moment().startOf("day").toDate()
            const meetingEnd = normalizedHighlightedRange.endDate

            if (todayDate > meetingEnd) {
                return base
            }

            base.meetingRange = {
                from: normalizedHighlightedRange.startDate,
                to: normalizedHighlightedRange.endDate
            }
        }
        return base
    }, [today, normalizedHighlightedRange])

    const modifiersStyles = useMemo(() => {
        const base: Record<string, React.CSSProperties> = {
            todaySelected: {
                color: "#185abc",
                backgroundColor: "#d2e3fc",
                maxWidth: "30px",
                maxHeight: "12px",
                minWidth: "30px",
                minHeight: "12px",
                borderRadius: "50%",
                width: "40px",
                height: "15px",
                border: "none",
                zIndex: 5
            }
        }

        if (normalizedHighlightedRange) {
            base.meetingRange = {
                backgroundColor: "#e6f7ff",
                color: "#185abc",
                borderRadius: "50%",
                zIndex: 1
            }
        }

        return base
    }, [normalizedHighlightedRange])

    return (
        <div className={styles.root}>
            <div className={cx(styles.indicatorWrap, {[styles.indicatorPanelWrap]: isPanelDisplay})}>
                {isPanelDisplay && (
                    <SecondaryButton
                        title=""
                        icon={
                            <Icon
                                icon="ARROW_LEFT"
                                className={styles.indicatorIcon}
                                color={isPrevMonthDisabled ? "#939393" : "var(--black-base)"}
                            />
                        }
                        className={cx(styles.indicatorIconBtn, {
                            [styles.indicatorIconBtnPanel]: isPanelDisplay
                        })}
                        onClick={onClickPrevMonth}
                        disabled={isPrevMonthDisabled}
                    />
                )}
                <div className={cx(styles.indicatorDate, {[styles.indicatorPanelDate]: isPanelDisplay})}>
                    {moment(dateViewed).format("MMMM YYYY")}
                </div>
                {!isPanelDisplay && (
                    <SecondaryButton
                        title=""
                        icon={
                            <Icon
                                icon="ARROW_LEFT"
                                className={styles.indicatorIcon}
                                color={isPrevMonthDisabled ? "#939393" : "var(--black-base)"}
                            />
                        }
                        className={cx(styles.indicatorIconBtn, {
                            [styles.indicatorIconBtnDisabled]: isPrevMonthDisabled
                        })}
                        onClick={onClickPrevMonth}
                        disabled={isPrevMonthDisabled}
                    />
                )}
                <SecondaryButton
                    title=""
                    icon={
                        <Icon
                            icon="ARROW_LEFT"
                            className={cx(styles.indicatorIcon, styles.rotate)}
                            color={isNextMonthDisabled ? "#939393" : "var(--black-base)"}
                        />
                    }
                    className={cx(styles.indicatorIconBtn, {
                        [styles.indicatorIconBtnPanel]: isPanelDisplay
                    })}
                    onClick={onClickNextMonth}
                    disabled={isNextMonthDisabled}
                />
            </div>
            <div className={isPanelDisplay ? "PanelDatePicker" : ""}>
                <DayPicker
                    className="w-full"
                    weekdaysShort={weekdaysShort}
                    showOutsideDays
                    month={dateViewed}
                    ref={pickerRef}
                    selectedDays={today}
                    modifiers={modifiers}
                    modifiersStyles={modifiersStyles}
                    onDayClick={onClick}
                    disabledDays={disabledDays}
                    firstDayOfWeek={0}
                    captionElement={() => null}
                />
            </div>
        </div>
    )
}
